import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { SoundService } from '../sound/sound.service';
import { Router } from '@angular/router';
import { ToasterService } from '../toaster/toaster.service';
import { IdentityService } from '../identity/identity.service';
import { SettingsService } from '../settings/settings.service';
import { PortraitService } from '../portraits/portrait.service';
import { GamesService } from '../games/games.service';
import { RECENT_ARENAS_KEY } from '../../models/types';
import gamesList from '../../../../public/games/games.json';
import { buildWebGameLaunchUrl } from '../../utils/game-launch.utils';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private router = inject(Router);
  private sound = inject(SoundService);
  private http = inject(HttpClient);
  private toaster = inject(ToasterService);
  private identity = inject(IdentityService);
  private settingsService = inject(SettingsService);
  private portraitService = inject(PortraitService);
  private gamesService = inject(GamesService);

  isConnected = signal<boolean>(false);
  arena = signal<any>(null);
  users = signal<any[]>([]);
  members = signal<any[]>([]);
  messages = signal<any[]>([]);
  rooms = signal<any[]>([]);
  games = signal<any[]>([]);
  isCurrentGameInstalled = signal<boolean>(true);
  selectedUserForModal = signal<any>(null);
  targetLockedArena = signal<any>(null);

  /*
   * Verify whether the game associated with the current lobby is installed.
   * Updates isCurrentGameInstalled signal state.
   */
  public async checkGameInstallation(): Promise<boolean> {
    const currentGames = this.games();
    if (!currentGames || currentGames.length === 0) {
      this.isCurrentGameInstalled.set(true);
      return true;
    }

    const firstGame = currentGames[0];
    if (firstGame && firstGame.gameType === 2) {
      const rawId = firstGame.gameId || firstGame.id;
      const gameIdStr = String(rawId);
      const targetId = gameIdStr === '5' ? 'quake-2' : (gameIdStr === '6' ? 'quake-1' : gameIdStr);

      let installed = await this.gamesService.isGameInstalled(targetId);
      if (!installed && targetId !== gameIdStr) {
        installed = await this.gamesService.isGameInstalled(gameIdStr);
      }

      this.isCurrentGameInstalled.set(installed);
      return installed;
    } else {
      this.isCurrentGameInstalled.set(true);
      return true;
    }
  }


  private connection!: HubConnection;
  private userDisconnected: boolean = false;
  private arenaPasswords = new Map<string, string>();
  private activeArenaId: string | null = null;

  constructor() {
    this.connection = new HubConnectionBuilder()
      .withUrl(environment.platformUrl + "arenaHub?user-client=rivalforge", {
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => {
          return JSON.parse(sessionStorage.getItem("token")!).token;
        }
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Custom retry delay up to 5 times.
          if (retryContext.previousRetryCount < 5) {
            return 2000;
          } else {
            return null; // Stop reconnecting.
          }
        }
      })
      .build();

    // Wire up subscriptions to hub methods.
    this.hubSubscriptions();
  }

  /**
   * Connect to messenger.
   * @returns 
   */
  public connect(arenaId: string, password: string = "") {
    this.arena.set(null);
    this.users.set([]);
    this.rooms.set([]);
    this.members.set([]);
    this.games.set([]);
    this.activeArenaId = arenaId;

    if (password) {
      this.arenaPasswords.set(arenaId, password);
    }

    const savedPassword = this.arenaPasswords.get(arenaId) ?? "";

    if (this.connection.state == HubConnectionState.Connected)
      return this.join(arenaId, savedPassword);

    this.isConnected.set(false);
    this.userDisconnected = false;

    return this.connection.start().then(() => {
      //console.log("Started signalr connection.");
      this.isConnected.set(true);
      this.join(arenaId, savedPassword);
    }).catch(error => {
      this.toaster.danger("Failed to connect to chat server.");
      throw error;
    });
  }

  /**
   * Disconnect from messenger.
   */
  public disconnect() {
    this.arena.set(null);
    this.users.set([]);
    this.rooms.set([]);
    this.members.set([]);
    this.games.set([]);
    this.activeArenaId = null;

    this.connection.stop();
    this.userDisconnected = true;
    this.isConnected.set(false);
  }

  /**
   * Create a new arena room.
   * @param arena 
   */
  public create(arena: any) {
    this.connection.invoke("create", arena);
  }

  /**
   * Join an arena.
   * @param id 
   * @param password 
   */
  public join(id: string, password: string): void {
    this.activeArenaId = id;
    if (password) {
      this.arenaPasswords.set(id, password);
    }
    this.connection.invoke("join", id, password);
  }

  public getPasswordForArena(arenaId: string): string | undefined {
    return this.arenaPasswords.get(arenaId);
  }

  /**
   * Leave an arena.
   */
  public leave() {
    this.connection.invoke("join", this.arena().parentGuid, "");
  }

  /**
   * Send message to arena.
   * @param text 
   */
  public send(text: string, attachment?: any) {
    // Prevent blank messages.
    if (text.replace(' ', '') == "" && !attachment)
      return;

    if (text.startsWith("/")) {
      console.log("Processing command.");
      this.sendCommand(text);
    }
    else {
      let message: any = {
        to: this.arena().guid,
        text: text,
        color: this.settingsService.fontColor()
      };

      if (attachment) {
        message.text = attachment.title;
        message.attachment = attachment;
      }

      console.log("sending message => ", message);
      this.connection.invoke('send', message);
    }
  }

  /**
   * Update an arena.
   * @param arena 
   */
  public update(arena: any) {
    this.connection.invoke("update", arena);
  }

  public updateUser() {
    if (this.connection.state == HubConnectionState.Connected) {
      console.log("Updating user avatar");
      this.connection.invoke("updateUser");
    }
  }

  /**
   * Launch a game on the hub.
   * @param gameMetaData 
   */
  public launch(gameMetaData: string) {
    if (this.connection.state == HubConnectionState.Connected) {
      console.log("Invoking launch method on the hub with metadata:", gameMetaData);
      this.connection.invoke("launch", gameMetaData);
    }
  }

  /**
   * Clear all messages in the local signal.
   */
  public clearMessages() {
    this.messages.set([]);
  }

  /**
   * Add a member to the arena.
   * @param arenaGuid 
   * @param member 
   */
  public addMember(arenaGuid: string, member: any) {
    this.connection.invoke("addMember", arenaGuid, member);
  }

  /**
   * Remove a member from the arena.
   * @param arenaGuid 
   * @param userId 
   */
  public removeMember(arenaGuid: string, userId: string) {
    this.connection.invoke("removeMember", arenaGuid, userId);
  }

  /**
   * Kick a user from the arena.
   * @param userId 
   */
  public kick(userId: string) {
    this.connection.invoke("kick", userId);
  }

  /**
   * Set user status on the hub.
   * @param status 
   */
  public setStatus(status: number) {
    if (this.connection.state == HubConnectionState.Connected) {
      this.connection.invoke("setstatus", status);
    }
  }

  /**
   * Send commands to the back-end.
   * @param text 
   */
  private sendCommand(text: string) {
    const [command, ...args] = text.split(" ");
    const argText = args.join(" ");

    const commands: Record<string, () => void> = {
      "/afk": () => {
        console.log("Sending afk");
        this.connection.invoke("setstatus", 2);
      },
      "/arena": () => {
        console.log("Sending arena message");
        this.connection.invoke("arenaMessage", argText);
      },
      "/busy": () => {
        console.log("Sending busy");
        this.connection.invoke("setstatus", 3);
      },
      "/back": () => {
        console.log("Sending back");
        this.connection.invoke("setstatus", 1);
      },
      "/ban": () => {
        const user = this.users().find(u => u.userName.toLowerCase().replace(' ', '') === argText.toLowerCase().replace(' ', ''));
        if (!user)
          return;

        this.connection.invoke("ban", argText);
      },
      "/kick": () => {
        const user = this.users().find(u => u.userName.toLowerCase().replace(' ', '') === argText.toLowerCase().replace(' ', ''));
        if (!user)
          return;

        this.connection.invoke("kick", argText);
      },
      "/me": () => {
        this.connection.invoke("sendAction", argText);
      },
      "/sys": () => {
        console.log("Sending system message");
        this.connection.invoke("systemMessage", argText);
      },
      "/enter": () => {
        this.sound.extraSoundForNow();
      },
      "/exit": () => {
        this.sound.playExitRoom();
      },
      "/play": () => {
        const soundName = args[0];
        if (soundName) {
          this.connection.invoke("play", soundName);
        }
      }
    };

    if (commands[command])
      commands[command]();
  }

  private emojiMap: Record<string, string> = {
    ':smile:': '😊',
    ':fire:': '🔥'
  };

  private parseEmojis(text: string): string {
    return text.replace(/:\w+:/g, (match) => {
      return this.emojiMap[match] || match;
    });
  }

  /**
   * Monitor hub method subscriptions.
   */
  private hubSubscriptions() {

    /**
     * Listen for reconnection.
     */
    this.connection.onreconnecting((error) => {
      this.isConnected.set(false);
      this.toaster.warning("Reconnecting to chat server...");
    });

    /**
     * Successful Reconnection
     */
    this.connection.onreconnected((connectionId) => {
      // Rejoin the arena.
      if (this.activeArenaId) {
        const password = this.arenaPasswords.get(this.activeArenaId) ?? "";
        this.join(this.activeArenaId, password);
      } else if (this.arena() && this.arena().guid) {
        this.join(this.arena().guid, "");
      } else if (this.arena() && this.arena().id) {
        this.join(this.arena().id, "");
      }
      this.isConnected.set(true);
      this.toaster.success("Reconnected to chat server.");
    });

    /**
     * Listen for close (final disconnect).
     */
    this.connection.onclose((error) => {
      if (!this.userDisconnected) {
        this.router.navigate(['/home/overview']);
        this.toaster.danger("Connection to chat server lost.");
      }
    });

    /**
     * Successfully joined an arena.
     */
    this.connection.on('joined', data => {
      console.log("joined => ", data);
      // Collect the data and sort the rooms.
      const arenaData = data;
      arenaData.arenas = arenaData.arenas.sort((a: any, b: any) => (a.creationDate < b.creationDate) ? -1 : 1);

      this.arena.set(arenaData.arena);
      this.users.set(arenaData.users);
      this.rooms.set(arenaData.arenas);

      if (this.arena().parentGuid == null) {
        this.members.set(arenaData.members);
        this.games.set(arenaData.games);
        this.checkGameInstallation();

        // Record locally (localStorage) the arena information when a user joins an arena.
        try {
          const stored = localStorage.getItem(RECENT_ARENAS_KEY);
          let list: any[] = [];
          if (stored) {
            list = JSON.parse(stored);
          }
          if (!Array.isArray(list)) {
            list = [];
          }
          list = list.filter((a: any) => a.guid !== arenaData.arena.guid);
          list.unshift({
            guid: arenaData.arena.guid,
            title: arenaData.arena.title,
            avatarURL: arenaData.arena.avatarURL,
            onlineUsers: arenaData.arena.onlineUsers
          });
          list = list.slice(0, 5);
          localStorage.setItem(RECENT_ARENAS_KEY, JSON.stringify(list));
        } catch (e) {
          console.error('Failed to save recent arena to localStorage', e);
        }
      }

      console.log("The arena membership is => ", this.members());

      // Add message of the day.
      let motd = {
        isSystemMessage: true,
        text: this.arena().motd
      };

      const updatedMessages = [...this.messages(), motd];
      this.messages.set(updatedMessages);
    });

    /**
     * Recieve a message in arena.
     */
    this.connection.on('message', message => {
      console.log("message received => ", message);
      message.timestamp = new Date();

      const fromSlug = message.from ? message.from.replace(' ', '').toLowerCase() : '';
      message.fromUser = this.users().filter(u => { return u.userNameSlug == fromSlug; })[0] || {
        userName: message.from || 'System',
        portraitId: 0,
        userNameColor: 'rgb(108, 117, 125)'
      };

      if (message.from === 'System' || message.from === 'SYSTEM' || message.isSystemMessage) {
        message.isSystemMessage = true;
      }

      message.text = this.parseEmojis(message.text);

      // Linkify detection
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const match = message.text.match(urlRegex);
      if (match) {
        const url = match[0];
        this.http.post(environment.platformUrl + 'api/chat/linkify', {
          url: url,
          title: "",
          description: "",
          image: ""
        }).subscribe({
          next: (res: any) => {
            if (res.image && typeof res.image === 'string') {
              res.image = [`data:image/png;base64,${res.image}`];
            }
            message.linkPreview = res;
            this.messages.set([...this.messages()]);
          },
          error: (err) => console.error("Linkify failed", err)
        });
      }

      // Play a sound if they are mentioned or aren't actively looking at the room.
      const claims = this.identity.getClaims();
      const currentUserName = claims?.unique_name;
      const isFromMe = currentUserName && message.from && (message.from.toLowerCase().trim() === currentUserName.toLowerCase().trim());

      if (currentUserName && message.text && !isFromMe && message.text.toLowerCase().includes(`@${currentUserName.toLowerCase()}`)) {
        this.sound.play('wakeup');
      } else if (this.arena().parentGuid != null && !this.router.url.startsWith("/home/chat")) {
        this.sound.playChime();
      }

      // Notify the Electron window when a message is received from another user.
      if (this.isElectron()) {
        (window as any).windowControls.notify();
      }

      if (!isFromMe && !message.isSystemMessage && document.hidden) {
        let title = `New message in ${this.arena()?.title || 'Arena'}`;
        let body = `${message.fromUser?.userName || 'Someone'}: ${message.text}`;
        let iconUrl = '/images/logo_1_4.png';

        if (message.fromUser?.portraitId) {
          const portrait = this.portraitService.portraits().find(e => e.id == message.fromUser.portraitId);
          if (portrait) {
            iconUrl = `https://static.h5arena.com/images/avatars/${portrait.fileName}`;
          }
        }

        this.toaster.desktopNotification(title, body, iconUrl, () => {
          if (this.arena()?.guid) {
            this.router.navigate(['/home/chat']);
          }
        });
      }

      //console.log(message);
      this.messages.set([...this.messages(), message]);
    });

    /**
     * Local action sent.
     */
    this.connection.on('sendaction', (actionMessage: string) => {
      console.log("Action message received => ", actionMessage);
      let systemMessage = {
        isSystemMessage: true,
        text: actionMessage,
        timestamp: new Date()
      };

      this.messages.set([...this.messages(), systemMessage]);
    });

    /**
     * Local lobby and rooms messages
     */
    this.connection.on('arenamessage', (text: string) => {
      console.log("Arena message received => ", text);
      let systemMessage = {
        isSystemMessage: true,
        text: text,
        timestamp: new Date()
      };

      this.messages.set([...this.messages(), systemMessage]);
    });

    /**
     * Receive a system message in arena.
     */
    this.connection.on('systemmessage', (text: string) => {
      console.log("System message received => ", text);
      let systemMessage = {
        isSystemMessage: true,
        text: text,
        timestamp: new Date()
      };

      this.messages.set([...this.messages(), systemMessage]);
    });

    /**
     * User joined arena.
     */
    this.connection.on('userJoined', user => {
      this.users.set([...this.users(), user]);

      // Play entrance sound and show message if not in lobby.
      if (this.arena().parentGuid != null) {
        let systemMessage = {
          isSystemMessage: true,
          text: `${user.userName} entered the room.`,
          timestamp: new Date()
        };

        this.messages.set([...this.messages(), systemMessage]);

        this.sound.play("roomentrance");
      }

    });

    /**
     * User left arena.
     */
    this.connection.on('userLeft', userName => {
      const users = this.users();
      let existing = users.filter((e) => { return e.userNameSlug == userName; })[0];
      if (!existing) return;

      let index = users.indexOf(existing);
      users.splice(index, 1);

      // Place exit sound.
      if (this.arena().parentGuid != null)
        this.sound.playExitRoom();

      this.users.set([...users]);
    });

    /**
     * Update members.
     */
    this.connection.on('updateMembers', (members: any[]) => {
      const currentMembers = this.members();
      const currentMemberIds = currentMembers.map(m => m.userId);
      const newMembers = members.filter(m => !currentMemberIds.includes(m.userId));
      this.members.set([...currentMembers, ...newMembers]);
    });

    /**
     * User has been updated.
     */
    this.connection.on('updateUser', user => {
      const users = this.users();
      let existing = users.filter((e) => { return e.userNameSlug == user.userNameSlug; })[0];
      if (!existing) return;

      let index = users.indexOf(existing);
      users.splice(index, 1);

      this.users.set([...users, user]);

    });

    /**
     * New room created.
     */
    this.connection.on('addArena', room => {
      this.rooms.set([...this.rooms(), room]);

      console.log("Added room => ", room);
    });

    /**
     * Room removed.
     */
    this.connection.on('removeArena', roomId => {
      const rooms = this.rooms();
      let existing = rooms.filter((e) => { return e.guid === roomId })[0];
      if (!existing) return;

      let index = rooms.indexOf(existing);
      rooms.splice(index, 1);

      this.rooms.set([...rooms]);
    });

    /**
     * Update a room in the rooms list.
     */
    this.connection.on('updateArena', room => {
      const rooms = this.rooms();
      let existing = rooms.filter((e) => { return e.guid === room.guid })[0];
      if (!existing) return;

      let index = rooms.indexOf(existing);
      rooms[index] = room;

      this.rooms.set([...rooms]);
    });

    /**
     * Handle arena updates.
     */
    this.connection.on('update', arena => {
      this.arena.set(arena);
    });

    this.connection.on('validationfailed', data => {
      console.error(data);
      const msg = typeof data === 'string' ? data : (data?.message || data?.error || "Validation failed.");
      this.toaster.danger(msg);
    });

    /**
     * Handle launch trigger from the hub.
     */
    this.connection.on('launch', (gameMetaData: string) => {
      console.log("Received launch notification from SignalR with metadata:", gameMetaData);

      let game = this.games()[0];
      if (!game) {
        console.error("No game found to launch.");
        return;
      }

      const gameId = game.gameId || game.id;
      const localGame = gamesList.find((g: any) => g.gameId === Number(gameId) || g.gameId === gameId);
      const baseUrl = localGame?.baseUrl || 'https://spades.rivalforge.com/game-board/';

      let metaPayload: any = {};
      if (typeof gameMetaData === 'string' && gameMetaData.trim().startsWith('{')) {
        try {
          metaPayload = JSON.parse(gameMetaData);
        } catch (e) {
          console.warn("Could not parse gameMetaData JSON:", e);
        }
      }

      switch (game.gameType) {
        case 0: // Web
          const webLaunchUrl = buildWebGameLaunchUrl({
            baseUrl,
            roomId: this.arena().guid,
            expectedHostUserId: metaPayload.expectedHostUserId || metaPayload.moderatorUserId || this.arena()?.ownerId,
            isQuickPlay: metaPayload.isQuickPlay !== undefined ? metaPayload.isQuickPlay : this.arena()?.isStatic,
            winScore: metaPayload.winScore,
            loseScore: metaPayload.loseScore,
            timer: metaPayload.timer,
            mode: metaPayload.mode
          });

          if (this.isElectron()) {
            const win = window as any;
            if (win.games.openWebGame) {
              win.games.openWebGame(webLaunchUrl);
            } else {
              console.warn("Electron environment detected, but openWebGame function was not found on global scope.");
            }
          } else {
            window.open(webLaunchUrl, '_blank');
          }
          break;
        case 2: // Native
          let payload: any = gameMetaData;
          if (typeof gameMetaData === 'string') {
            try {
              payload = JSON.parse(gameMetaData);
            } catch (e) {
              console.warn("Could not parse gameMetaData JSON:", e);
            }
          }

          const gameKey = payload?.game || payload?.gameId || gameId;
          const targetIp = payload?.publicIp || payload?.ip;
          const extraArgs: string[] = [];

          if (targetIp) {
            extraArgs.push('+connect', targetIp);
          }

          if (payload?.mod && payload.mod !== 'baseq2' && payload.mod !== 'id1' && payload.mod !== 'baseq3') {
            extraArgs.unshift('+set', 'game', payload.mod);
          }

          console.log(`Native launch trigger for ${gameKey} with args:`, extraArgs);
          this.gamesService.launchGame(String(gameKey), extraArgs).then(success => {
            if (success) {
              this.toaster.success(`Launching ${gameKey} (connecting to ${targetIp || 'server'})...`);
            } else {
              this.toaster.warning(`Could not launch ${gameKey}. Please verify game executable path in Library.`);
            }
          }).catch(err => {
            console.error(`Error launching game ${gameKey}:`, err);
          });
          break;
        default:
          break;
      }
    });

    /**
     * Handle play sound request from the hub.
     */
    this.connection.on('play', (soundName: string) => {
      console.log("Received play sound request from hub:", soundName);
      this.sound.play(soundName);
    });
  }

  private isElectron(): boolean {
    return navigator.userAgent.toLowerCase().includes('electron');
  }
}
